
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { CompareArrowsIcon } from './icons';

interface ImageComparatorProps {
  beforeSrc: string;
  afterSrc: string;
}

export const ImageComparator: React.FC<ImageComparatorProps> = ({ beforeSrc, afterSrc }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = (x / rect.width) * 100;
    setSliderPosition(percent);
  }, [isDragging]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseUp = () => setIsDragging(false);
    const handleTouchEnd = () => setIsDragging(false);
    
    // Add listeners to the window to handle dragging outside the component
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('mouseleave', handleDragEnd);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('mouseleave', handleDragEnd);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full select-none overflow-hidden rounded-xl flex items-center justify-center"
      onMouseMove={handleDragMove}
      onTouchMove={handleDragMove}
    >
      <img src={beforeSrc} alt="Before" className="block max-w-full max-h-full object-contain pointer-events-none" />
      <div
        className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden"
        style={{ clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)` }}
      >
        <img src={afterSrc} alt="After" className="absolute top-0 left-0 w-full h-full object-contain pointer-events-none" />
      </div>
      <div
        className="absolute top-0 bottom-0 w-1 bg-white/70 backdrop-blur-sm cursor-ew-resize group"
        style={{ left: `calc(${sliderPosition}% - 0.5px)` }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/50 border-2 border-white flex items-center justify-center text-black transition-transform group-hover:scale-110">
          <CompareArrowsIcon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};
